import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a seamless, continuous marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {string} */
  #lastKey = '';

  /** @type {number | null} */
  #lastSpeed = null;

  /** @type {{ cancel: () => void, current: number } | null} */
  #animation = null;

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems, content } = this.refs;
    if (marqueeItems.length === 0) return;

    // Re-measures when the viewport, images or fonts change the item width
    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this);
    if (content.firstElementChild) this.#resizeObserver.observe(content.firstElementChild);

    if (this.dataset.pauseOnHover === 'true') {
      this.addEventListener('pointerenter', this.#onEnter);
      this.addEventListener('pointerleave', this.#onLeave);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#lastKey = '';
    this.#lastSpeed = null;
    this.#slowDown.cancel();
    this.#animation?.cancel();
    this.#animation = null;
    this.removeEventListener('pointerenter', this.#onEnter);
    this.removeEventListener('pointerleave', this.#onLeave);
  }

  #onEnter = () => this.#slowDown();
  #onLeave = () => this.#speedUp();

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * Rebuilds copies so that one content block is always at least as wide as the viewport.
   */
  #update = () => {
    const { content } = this.refs;
    const firstItem = content.firstElementChild;
    if (!firstItem) return;

    const viewWidth = this.getBoundingClientRect().width;
    const itemWidth = firstItem.getBoundingClientRect().width;
    if (!viewWidth || !itemWidth) return;

    const key = `${viewWidth}|${itemWidth}`;
    if (key === this.#lastKey) return;
    this.#lastKey = key;

    const gap = parseFloat(getComputedStyle(content).columnGap) || 0;
    const copies = Math.max(1, Math.ceil((viewWidth + gap) / (itemWidth + gap)));
    const current = content.children.length;

    if (copies > current) {
      this.#addRepeatedItems(copies - current);
    } else if (copies < current) {
      this.#removeRepeatedItems(current - copies);
    }

    this.#duplicateContent();
    this.#applySpeed(this.#calculateSpeed(copies));
  };

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    return Math.sqrt(numberOfCopies) * speedFactor;
  }

  /**
   * Updates the duration and keeps the current position so there is no jump.
   * @param {number} speed
   */
  #applySpeed(speed) {
    if (speed === this.#lastSpeed) return;
    this.#lastSpeed = speed;

    const { wrapper } = this.refs;
    const before = wrapper.getAnimations()[0];
    const progress = before?.effect?.getComputedTiming().progress ?? null;

    this.style.setProperty('--marquee-speed', `${speed}s`);

    if (progress === null) return;

    const after = wrapper.getAnimations()[0];
    const duration = after?.effect?.getComputedTiming().duration;

    if (after && typeof duration === 'number') {
      after.currentTime = progress * duration;
    }
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} count
   */
  #addRepeatedItems(count) {
    const { content } = this.refs;
    const source = content.firstElementChild;
    if (!source) return;

    for (let i = 0; i < count; i++) {
      content.appendChild(source.cloneNode(true));
    }
  }

  /**
   * @param {number} count
   */
  #removeRepeatedItems(count) {
    const { content } = this.refs;
    const itemsToRemove = Math.min(count, content.children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

/**
 * Animate a numeric property smoothly.
 * @param {Object} params
 * @param {number} params.from
 * @param {number} params.to
 * @param {number} params.duration
 * @param {function(number): void} params.onUpdate
 * @param {function(number): number} [params.easing]
 * @param {function(): void} [params.onComplete]
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}